// ════════════════════════════════════════════════════════════════════════════
//  features/renave/TransferenciaRenave.tsx — saída por TRANSFERÊNCIA (Fatia 4).
//
//  Outra revenda: a ORIGEM (esta tela) autoriza o CNPJ de destino + valor; o
//  destino solicita o recebimento no sistema dele. Filial (mesmo CNPJ raiz):
//  não há autorização — a filial de destino usa "Receber de filial".
//  Depois que o destino recebe, o veículo fica Transferido e falta a NF-e de saída.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Ban, FileSearch, FileText, Loader2, Send, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/sonner';
import {
  renaveApi, formatarCnpj, formatarMoeda, SITUACAO,
  type ConferenciaFiscal, type ResultadoOperacao, type VeiculoEstoque,
} from './api';
import { ConferenciaFiscalPainel } from './ConferenciaFiscalPainel';

interface Props {
  v: VeiculoEstoque;
  ocupado: string | null;
  travado: boolean;
  exigirCpf: () => string | null;
  escrever: (rotulo: string, acao: () => Promise<ResultadoOperacao>) => Promise<void>;
  ler: <T>(rotulo: string, acao: () => Promise<T>, aoTerminar: (t: T) => void) => Promise<void>;
}

export function TransferenciaRenave({ v, ocupado, travado, exigirCpf, escrever, ler }: Props) {
  const emEstoque   = v.situacao === SITUACAO.EmEstoque;
  const transferido = v.situacao === SITUACAO.Transferido;
  const autorizada  = v.estadoAutorizacao === 'CRIADA';

  const [cnpj, setCnpj]   = useState('');
  const [valor, setValor] = useState('');
  const [chave, setChave] = useState('');
  const [conferencia, setConferencia] = useState<ConferenciaFiscal | null>(null);

  const spin = (r: string, Icone: LucideIcon) =>
    ocupado === r ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Icone className="mr-2 h-4 w-4" />;

  if (!emEstoque && !transferido) return null;

  const autorizar = () => {
    const valorNum = Number(valor.replace(/\./g, '').replace(',', '.'));
    if (cnpj.length !== 14) return void toast.error('Informe o CNPJ (14 dígitos) da revenda de destino.');
    if (!(valorNum > 0)) return void toast.error('Informe o valor da transferência.');
    const cpf = exigirCpf(); if (!cpf) return;
    void escrever('autorizar', () => renaveApi.autorizarTransferencia(v.id, cnpj, valorNum, cpf));
  };

  return (
    <div className="space-y-4">
      {emEstoque && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Transferir para outra revenda</CardTitle>
            <CardDescription>
              Esta revenda autoriza o CNPJ de destino; a revenda de destino solicita o recebimento no sistema dela. O veículo
              continua aqui até ela receber. Exige o estoque CONFIRMADO pelo Detran, a NF-e de entrada e a assinatura do ATPV já enviadas.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {autorizada ? (
              <>
                <p className="text-sm">
                  Autorizada para <strong>{formatarCnpj(v.cnpjDestinoAutorizacao)}</strong> por <strong>{formatarMoeda(v.valorAutorizacao)}</strong>
                  {v.renaveIdAutorizacao ? <span className="text-muted-foreground"> (autorização nº {v.renaveIdAutorizacao})</span> : null}.
                  Aguardando a revenda de destino. Depois que ela receber, use "Atualizar situação" na aba de entrada (ou a conciliação).
                </p>
                <Button variant="destructive" size="sm" disabled={travado}
                  onClick={() => { const cpf = exigirCpf(); if (cpf) void escrever('cancelar-aut', () => renaveApi.cancelarAutorizacao(v.id, cpf)); }}>
                  {spin('cancelar-aut', Ban)}Cancelar autorização
                </Button>
              </>
            ) : (
              <div className="flex flex-wrap items-end gap-3">
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium">CNPJ da revenda de destino</span>
                  <Input value={cnpj} onChange={e => setCnpj(e.target.value.replace(/\D/g, ''))} maxLength={14}
                    inputMode="numeric" className="w-48 font-mono" />
                </label>
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium">Valor (vai para a intenção de venda)</span>
                  <Input value={valor} onChange={e => setValor(e.target.value)} inputMode="decimal" className="w-40" />
                </label>
                <Button size="sm" disabled={travado} onClick={autorizar}>{spin('autorizar', Send)}Autorizar transferência</Button>
                {v.estadoAutorizacao && (
                  <span className="text-xs text-muted-foreground">Última autorização: {v.estadoAutorizacao.toLowerCase()}.</span>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {emEstoque && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Transferir para uma filial</CardTitle>
            <CardDescription>
              Entre filiais (mesmo CNPJ raiz) não há autorização: quem registra é a filial de destino, em
              Estoque de veículos → <strong>Receber de filial</strong>. O RENAVE não emite CRV novo e o estoque já nasce confirmado.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {transferido && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">NF-e de saída da transferência</CardTitle>
            <CardDescription>
              O veículo foi recebido por {formatarCnpj(v.documentoComprador)}
              {v.valorVenda != null ? ` (${formatarMoeda(v.valorVenda)})` : ''}. Emita a NF-e de saída para esse CNPJ
              (5.152/6.152 entre filiais, 5.102/6.102 para outra revenda) e envie a chave ao RENAVE.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {v.chaveNfSaida && <p className="break-all font-mono text-xs">Enviada: {v.chaveNfSaida}</p>}
            <div className="flex flex-wrap gap-2">
              <Input value={chave} onChange={e => { setChave(e.target.value.replace(/\D/g, '')); setConferencia(null); }} maxLength={44}
                inputMode="numeric" placeholder="Chave de 44 dígitos" className="w-[26rem] max-w-full font-mono text-xs" />
              <Button variant="outline" size="sm" disabled={!!ocupado}
                onClick={() => ler('conf-transf', () => renaveApi.conferenciaFiscal(v.id, 'VENDA', chave || undefined),
                  c => { setConferencia(c); if (c.nota) setChave(c.nota.chave); })}>
                {spin('conf-transf', FileSearch)}{chave ? 'Conferir NF-e' : 'Buscar NF-e no Gestor'}
              </Button>
              <Button size="sm" disabled={travado || chave.length !== 44 || (conferencia?.bloqueios.length ?? 0) > 0}
                onClick={() => { const cpf = exigirCpf(); if (cpf) void escrever('nf-transf', () => renaveApi.notaFiscalSaida(v.id, chave, cpf)); }}>
                {spin('nf-transf', FileText)}{v.chaveNfSaida ? 'Trocar chave' : 'Enviar NF-e'}
              </Button>
            </div>
            {conferencia && <ConferenciaFiscalPainel c={conferencia} />}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
